import { Product, ProductSlug } from "@/api/product-api";
import Container from "@/components/commons/Container";
import BhapticsCompanionSoftware from "@/components/tactsuit/components/BhapticsCompanionSoftware";
import {
  CoolingFanHowToSection,
  CoolingFanIntroSection,
  CoolingFanSpecsSection,
} from "@/components/tactsuit/cooling-fan/cooling-fan-feature-sections";
import ImmersionBeyondComparison from "@/components/tactsuit/components/ImmersionBeyondComparison";
import OfficiallyMetaCertified from "@/components/tactsuit/components/OfficiallyMetaCertified";
import TactsuitTechSpecs from "@/components/tactsuit/components/TactsuitTechSpecs";
import Summary from "@/components/tactsuit/Summary";
import TactSuitAirContent from "@/v2-components/pages/tactsuit/tactsuit-air/TactSuitAirContent";
import TactSuitAirCamoContent from "@/v2-components/pages/tactsuit/tactsuit-air/TactSuitAirCamoContent";
import InstallationGuide from "@/components/tactsuit/InstallationGuide";
import Dk3Technology from "@/components/tactsuit/tactglove-dk3/Dk3Technology";
import Dk3VrchatStory from "@/components/tactsuit/tactglove-dk3/Dk3VrchatStory";
import Dk3Comparison from "@/components/tactsuit/tactglove-dk3/Dk3Comparison";
import Dk3Performance from "@/components/tactsuit/tactglove-dk3/Dk3Performance";
import Dk3UseCases from "@/components/tactsuit/tactglove-dk3/Dk3UseCases";
import Dk3Specs from "@/components/tactsuit/tactglove-dk3/Dk3Specs";
import Dk3SizeGuide from "@/components/tactsuit/tactglove-dk3/Dk3SizeGuide";
import { ComponentId } from "@/constants";
import { cn } from "@/libs/utils";
import { useLocale } from "@/store/locale-store";
import { ProductUtil } from "@/utils/ProductUtil";
import { skuOptionHasColor } from "@/utils/product-colors";
import { useBuyTactSuit } from "@/store/useBuyTactSuit";
import { useTrans } from "@/utils/useTrans";
import FeatureCards from "@/components/tactsuit/FeatureCards";
import BhapticsContentCards from "@/components/tactsuit/BhapticsContentCards";
import TactvisorSizeGuide from "@/v2-components/size-guide-sheet/TactvisorSizeGuide";
import { SleeveProductFeatures } from "@/components/tactsuit/sleeve-feature-sections";
import { Gap } from "@/components/tactsuit/Gap";
import { PDP_TEXT_LINK } from "@/components/tactsuit/pdp-text-link";
import { useProInteractionViewport } from "@/v2-components/pages/tactsuit/tactsuit-pro/pro-interaction-viewport";
import ContentsService from "@/services/ContentsService";
import { useProductFeatures } from "@/components/tactsuit/product-features";
import { AudioToHapticSection } from "@/v2-components/pages/tactsuit/audio-to-haptic-section";
import { WirelessFeature } from "@/v2-components/pages/tactsuit/GridsCard";
import ProInteractionA from "@/v2-components/pages/tactsuit/tactsuit-pro/pro-interaction-a";
import { DisplayGameCardsCarousel } from "@/v2-components/pages/games/DisplayGameCardsCarousel";
import Image from "next/image";
import Link from "next/link";
import React from "react";
import ProductsInTheBox from "@/components/tactsuit/components/ProductsInTheBox";
import { useInTheBoxProducts } from "@/components/tactsuit/components/useInTheBoxProducts";
import ChevronDownIcon from "@/v2-components/core/icon/chevron-down-icon";
import {
  ExoshockBadgeSection,
  ExoshockBundleContents,
  ExoshockEventBanner,
  ExoshockGamekeySection,
  ExoshockMeshLiningSection,
  ExoshockNativeHapticsSection,
  ExoshockPlatformNote,
} from "@/components/tactsuit/components/buy-tactsuit/exoshock-bundle-sections";
import {
  BADGE_DETAIL_ANCHOR,
  EXOSHOCK_COMPONENT_SKU,
  MESH_LINING_DETAIL_ANCHOR,
  scrollToDetail,
} from "@/components/tactsuit/components/buy-tactsuit/bundle-detail-rail";

/**
 * 제품 소개 콘텐츠를 구매 상세 페이지 하단에 임베드하는 섹션.
 * 순서(프로토타입 01-buy-tactsuit-pro 플로우 기준):
 * Precise Directional Feedback(히어로 대체) → 구매 혜택(부담 없이 체험, 프로토타입 순서 그대로 앞쪽 배치) →
 * 기존 제품 소개 페이지(/tactsuit/tactsuit-pro) 상세 설명 섹션 동일 적용 →
 * 비교 겸 스펙(Air vs Pro) → Made for Meta(가장 하단)
 */
/**
 * memo 필수 — [slug].tsx 의 zustand 셀렉터가 매 mutation 마다 새 객체를 반환해서
 * 색상 스와치·액세서리 수량·아코디언 클릭 하나하나가 페이지를 리렌더한다. memo 가 없으면
 * 그때마다 이 아래의 구 소개페이지 트리 전체(비디오 4개 + ProInteractionA 포함)가 딸려 온다.
 * product/products/packageDeals 는 SSR 산출물이라 참조가 안정적이고, onAddBadge 는
 * 호출처에서 useCallback 으로 고정한다.
 */
export const ProductFeatureSections = React.memo(
  function ProductFeatureSections({
    product,
    products = [],
    packageDeals = [],
    onAddBadge,
  }: {
    product: Product;
    products?: Product[];
    packageDeals?: Product[];
    onAddBadge?: () => void;
  }) {
    switch (product.slug) {
      case ProductSlug.TACTSUIT_PRO:
        return (
          <TactsuitProFeatureSections
            product={product}
            products={products}
            packageDeals={packageDeals}
            onAddBadge={onAddBadge}
          />
        );
      case ProductSlug.TACTSLEEVE:
        return (
          <TactSleeveFeatureSections
            product={product}
            products={products}
            packageDeals={packageDeals}
            onAddBadge={onAddBadge}
          />
        );
      case ProductSlug.COOLING_FAN:
        return (
          <CoolingFanFeatureSections
            product={product}
            products={products}
            onAddBadge={onAddBadge}
          />
        );
      case ProductSlug.TACTVISOR:
      case ProductSlug.TACTOSY_FOR_FEET:
        return (
          <AccessoryDeviceFeatureSections
            product={product}
            products={products}
            packageDeals={packageDeals}
            onAddBadge={onAddBadge}
          />
        );
      case ProductSlug.TACTSUIT_AIR:
      case ProductSlug.TACTSUIT_AIR_CAMO:
        return (
          <TactSuitAirFeatureSections
            product={product}
            products={products}
            packageDeals={packageDeals}
            onAddBadge={onAddBadge}
          />
        );
      case ProductSlug.EXOSHOCK_BUNDLE:
        return (
          <ExoshockBundleFeatureSections
            product={product}
            products={products}
          />
        );
      case ProductSlug.TACTGLOVE_DK3:
        return (
          <TactGloveDk3FeatureSections
            product={product}
            products={products}
            onAddBadge={onAddBadge}
          />
        );
      default:
        // ⚠️ `null` 이 아니다. 여기서 `null` 을 돌려주면 **mgmt 에 새 장비를 등록하는
        // 순간 구매 레일 아래가 통째로 빈다** — 45일 보장·워런티·KR 무료배송·무료
        // 배지 카드까지 사라진다. 그런데 `ProductHeroAndBenefits` 는 자기 docblock 에
        // **"제품 무관 공용 블록"** 이라고 적고 있고 배지 이미지도 제품별 자동 반영이다.
        // 즉 슬러그 화이트리스트 뒤에 가둘 이유가 없다.
        //
        // 위 case 들은 그 공용 블록 **위에 얹는 제품별 마케팅 섹션**이다.
        // `tsc`·lint·테스트가 전부 침묵하는 종류라 회귀 테스트로 잠갔다
        // (`__tests__/components/product-feature-sections-default.test.tsx`).
        //
        // ⚠️ `HeroCopy` 의 `default: return null` 은 **그대로 두는 게 맞다** — 국문
        // 카피가 없는 제품에 임의 번역을 만들지 않겠다는 결정이고, 히어로 자체는
        // `product.bannerImage` 폴백이 있다. 두 switch 를 혼동하지 말 것.
        //
        // ⚠️ **컴포넌트로 빼 둔다.** `switch` 안에서는 훅을 못 부르고, 무엇보다 회귀
        // 가드(`in-the-box-section.test.ts`)가 `const *FeatureSections` 선언을 열거하므로
        // 인라인으로 두면 이 경로가 **가드 밖**에 남는다 — DK3 와 같은 실패 모드가 신규
        // 제품 방향으로 다시 열린다.
        return (
          <DefaultFeatureSections
            product={product}
            products={products}
            onAddBadge={onAddBadge}
          />
        );
    }
  },
);

// 히어로 — 현재 웹사이트 제품 상세 대형 배너 이미지 사용 (모바일은 제품 중심 크롭 변형)
const HERO_BANNER_URL = "/images/tactsuit/tactsuit-pro/cover_01_desktop@2x.jpg";
const HERO_BANNER_MOBILE_URL =
  "/images/tactsuit/tactsuit-pro/cover_01_mobile@3x.jpg";
const FEATURE_02_URL = "/images/tactsuit/tactsuit-pro/wireless.png";

type HeroConfig = {
  /** 상단 아이브로우(제품명). 번역 대상 아님 — 제품명 고유 표기 */
  eyebrow: string;
  /** mgmt `bannerImage` 미등록 제품용 로컬 폴백. 등록돼 있으면 서버 값이 우선한다. */
  bannerDesktop?: string;
  bannerMobile?: string;
};

/**
 * 제품별 히어로 설정 — 아이브로우 + (필요 시) 로컬 배너 폴백.
 *
 * 배너 이미지는 **mgmt `ShopProduct.bannerImage`(CDN)** 가 단일 소스다. 소개 페이지의
 * `ProductBanner`(components/tactsuit/main/ProductBanner.tsx)와 같은 출처라 두 화면이
 * 자동으로 일치하고, mgmt 에서 배너를 교체하면 배포 없이 반영된다. 2026-08 기준 5개
 * 제품 모두 등록돼 있어 여기 로컬 경로는 비워 둔다.
 *
 * 디자인팀에서 제품별 이미지를 리포로 받게 되면 `bannerDesktop`/`bannerMobile` 에
 * 로컬 경로를 채우면 된다 — 단, 그러면 mgmt 값이 우선하므로 서버 쪽도 함께 정리할 것.
 *
 * 카피는 번역 키가 코드에 리터럴로 남아야 해서(docs/i18n.md) 아래 `HeroCopy` 가 담당한다.
 */
const HERO_BY_SLUG: Partial<Record<string, HeroConfig>> = {
  [ProductSlug.TACTSUIT_PRO]: {
    eyebrow: "TactSuit Pro",
    // Pro 만 리포에 자산이 있어 폴백으로 유지 (mgmt 값이 우선)
    bannerDesktop: HERO_BANNER_URL,
    bannerMobile: HERO_BANNER_MOBILE_URL,
  },
  [ProductSlug.TACTSLEEVE]: { eyebrow: "TactSleeve" },
  [ProductSlug.TACTVISOR]: { eyebrow: "TactVisor" },
  [ProductSlug.TACTGLOVE_DK3]: { eyebrow: "TactGlove DK3" },
  [ProductSlug.TACTOSY_FOR_FEET]: { eyebrow: "Tactosy for Feet" },
  [ProductSlug.TACTSUIT_AIR]: { eyebrow: "TactSuit Air" },
  [ProductSlug.TACTSUIT_AIR_CAMO]: { eyebrow: "TactSuit Air: Camo" },
};

/**
 * 히어로 헤드라인/서브 — 기획 확정 카피.
 * 출처: 「디자인」Website(8X5fPtMR3n9lVij5MJKaUh) 제품별 상세기획서의
 * `· EN: <헤드라인> / <서브>` 명세. 헤드라인은 기획 파일(p47GZ) 히어로 프레임명과 동일.
 *
 * ⚠️ `t()` 는 **리터럴 인자**로만 호출한다 — 변수로 키를 넘기면 grep 기반 미사용 키 판정이
 * 오작동한다(docs/i18n.md §번역 키는 코드에 리터럴로 남겨야 한다). 그래서 맵이 아니라 switch.
 *
 * KR 값은 Pro 만 기획서에 `· KR:` 로 확정돼 있어 반영됨. 나머지 4개는 국문 카피 미확정이라
 * `tactsuit.json` kr 에 영문을 그대로 뒀다 — 임의 번역을 만들지 않는다.
 */
const HeroCopy = ({ slug }: { slug: string }) => {
  const { t } = useTrans(["tactsuit", "shop"]);

  switch (slug) {
    case ProductSlug.TACTSUIT_PRO:
      return (
        <>
          <HeroHeadline>{t("Feel Every Hit, Every Moment")}</HeroHeadline>
          <HeroSub>
            {t(
              "Every shot and explosion, delivered to the exact spot on your body.",
            )}
          </HeroSub>
        </>
      );
    case ProductSlug.TACTSLEEVE:
      return (
        <>
          <HeroHeadline>{t("Every Shot, Down Your Arms")}</HeroHeadline>
          <HeroSub>
            {t("Recoil and impact delivered straight to your arms.")}
          </HeroSub>
        </>
      );
    case ProductSlug.TACTVISOR:
      return (
        <>
          <HeroHeadline>{t("Feel It Coming, Face First")}</HeroHeadline>
          <HeroSub>{t("Head haptics that put you inside the blast.")}</HeroSub>
        </>
      );
    case ProductSlug.TACTGLOVE_DK3:
      return (
        <>
          <HeroHeadline>{t("Touch the Virtual World")}</HeroHeadline>
          {/* 기획 카피가 2줄 — 줄바꿈은 키 안의 \n + HeroSub 의 whitespace-pre-line 으로 보존 */}
          <HeroSub>
            {t(
              "Sketch and feel any haptic pattern instantly.\nBuilt to power XR training, simulation, and high-level research.",
            )}
          </HeroSub>
        </>
      );
    case ProductSlug.TACTOSY_FOR_FEET:
      return (
        <>
          <HeroHeadline>{t("Ground Every Step")}</HeroHeadline>
          <HeroSub>
            {t("Feel footsteps, drops, and rumble beneath you.")}
          </HeroSub>
        </>
      );
    case ProductSlug.TACTSUIT_AIR:
      return (
        <>
          <HeroHeadline>{t("Feel the Game, Light as Air")}</HeroHeadline>
          <HeroSub>
            {t("Stay comfortable through the longest play sessions.")}
          </HeroSub>
        </>
      );
    case ProductSlug.TACTSUIT_AIR_CAMO:
      return (
        <>
          <HeroHeadline>{t("Blend In. Feel Everything.")}</HeroHeadline>
          <HeroSub>
            {t("The Camo edition — stealth looks with full-body immersion.")}
          </HeroSub>
        </>
      );
    default:
      return null;
  }
};

// 모바일 22/28 · 데스크톱 32/40, 둘 다 **w400**(28718:166009 · 28713:134656).
// ⚠️ `leading-*` 를 붙이지 말 것 — 이 프로젝트의 `fontSize` 스케일이 행간을 함께 갖는다
// (`2xl` = 22/28, `5xl` = 32/40). `leading-10`(40) 은 `text-5xl` 이 이미 주는 값이라
// 중복이다(#5156 리뷰 지적). 임의값(`text-[32px]`)을 쓰는 다른 파일들은 행간이 안 따라와
// `leading-10` 이 필요하지만, 스케일 클래스를 쓰면 불필요하다.
// `uppercase` 는 유지 — 한국어·일본어·중국어는 대소문자가 없어 그대로 통과한다.
// ⚠️ 간격은 부모 컨테이너의 `gap` 이 갖는다(모바일 8 / 데스크톱 16). 여기 `mt-*` 를
// 두면 한 값이 세 클래스에 흩어지고 breakpoint 마다 중화가 필요해진다.
const HeroHeadline = ({ children }: React.PropsWithChildren) => (
  <h2 className="text-2xl font-400 uppercase text-white tablet:text-5xl">
    {children}
  </h2>
);

// whitespace-pre-line: DK3 처럼 기획 카피가 2줄인 경우 키 안의 \n 을 줄바꿈으로 보존한다.
// 모바일 14/18 ls.25 · 데스크톱 16/22 ls.5, 양쪽 모두 **흰색 100%**
// (28718:166010 · 28713:134654). 간격은 부모 `gap` 이 갖는다.
const HeroSub = ({ children }: React.PropsWithChildren) => (
  <p className="max-w-[640px] whitespace-pre-line text-base tracking-[0.25px] text-white tablet:text-lg tablet:tracking-[0.5px]">
    {children}
  </p>
);

/**
 * 히어로 + 구매 혜택 카드 — 제품 무관 공용 블록.
 * 배지 카드 이미지는 해당 제품의 배지 악세사리 썸네일을 쓰므로 제품별로 자동 반영된다.
 */
/** 배지 카드 자리를 대신하는 혜택 카드(번들 전용 PDP) — `BenefitCard` 의 문구·이미지 props 그대로 */
type FeaturedBenefitCard = Pick<
  React.ComponentProps<typeof BenefitCard>,
  "image" | "imageClassName" | "title" | "description" | "link"
>;

const ProductHeroAndBenefits = ({
  product,
  products,
  onAddBadge,
  heroOverride,
  featuredCard,
}: {
  product: Product;
  products: Product[];
  /** 배지 추가 아코디언을 열고 그 위치로 스크롤한다(피그마 28683:7792 주석). */
  onAddBadge?: () => void;
  /** 공용 히어로(1440 카드형) 대신 그릴 히어로. 주면 `bannerImage` 히어로를 그리지 않는다. */
  heroOverride?: React.ReactNode;
  /** 배지 카드 대신 그릴 카드. 주면 배지 카드는 그리지 않고 이 카드가 그 자리(마지막)에 온다. */
  featuredCard?: FeaturedBenefitCard;
}) => {
  const { t } = useTrans(["tactsuit", "shop"]);
  // 한국 배송 지역에서만 무료 배송 혜택 카드 추가 노출
  const locale = useLocale();
  const isKorea = locale.countryCode === "KR";

  // 히어로 배너는 **mgmt `ShopProduct.bannerImage`(CDN)** 를 단일 소스로 쓴다.
  // 소개 페이지의 ProductBanner 와 같은 출처라 두 화면이 자동으로 일치한다.
  // 하드코딩 경로를 쓰면 제품이 늘 때마다 코드 수정이 필요하고, mgmt 에서 배너를
  // 교체해도 구매 페이지만 옛 이미지로 남는다.
  // bannerImage 미등록 제품만 HERO_BY_SLUG 의 로컬 경로로 폴백.
  const hero = HERO_BY_SLUG[product.slug];
  const bannerDesktop =
    product.bannerImage?.desktopSrc || hero?.bannerDesktop || "";
  const bannerMobile =
    product.bannerImage?.mobileSrc || hero?.bannerMobile || bannerDesktop;

  // 배지 무료 증정 카드 이미지 — 해당 제품의 배지 악세사리 썸네일을 제품별로 노출
  const { badgeAccessories } = ProductUtil.getAccessoriesForProduct(
    products,
    product.slug,
  );
  /**
   * 배지 카드 커버 — **선택한 색상의 배지 이미지**를 쓴다.
   *
   * 예전엔 `slug.includes("original")` 로 첫 배지의 `skuOptions[0]` 을 집었는데,
   * "original" 이 두 제품에 걸린다 — `original-badge-for-tactsuit-air`(Ash/Onyx)와
   * `original-camo-badge-for-tactsuit-air`(Camo). 배열 순서상 camo 가 먼저라
   * **기본·onyx 를 골라도 Camo 배지 이미지가 떴다**(실측: 3개 URL 모두 Camo-OG.png).
   * 둘 다 `accessoryFor` 에 `tactsuit-air`·`tactsuit-air-camo` 를 모두 갖고 있어
   * 데이터로는 못 가른다(camo 배지가 기본 Air 에서도 실제로 팔리기 때문).
   *
   * ⚠️ 색상만 store 에서 좁게 구독한다. 이 트리는 `React.memo` 로 감싸 옵션 변경마다
   * 리렌더되는 걸 막고 있는데(위 memo 주석), 원시값 셀렉터는 **그 값이 바뀔 때만**
   * 리렌더하므로 색상 전환 = 정확히 다시 그려야 하는 시점이다.
   */
  const selectedColor = useBuyTactSuit(
    (s) => s.options.find((o) => o.type === "color")?.value,
  );
  const originalBadges = badgeAccessories.filter((p) =>
    p.slug.includes("original"),
  );
  // ⚠️ **색상 경계를 넘는 폴백을 두지 않는다.** `?? originalBadges[0]` 를 두면
  // camo 배지만 남았을 때 onyx 화면에 Camo 배지가 다시 뜬다 — 이 블록이 없애려던
  // 바로 그 증상이다. 색상 매칭 실패 = 정적 폴백 이미지가 낫다.
  // 반면 `original` 배지가 아예 없는 제품은 색상 정보 자체가 없으니
  // 첫 배지로 떨어져도 색이 어긋날 여지가 없다.
  // ⚠️ camo 는 **`product.slug` 로 판정**한다 — 이 prop 은 페이지가 넘기는 실효 제품이라
  // SSR·첫 페인트에도 정확하다. store 만 보면 `[slug].tsx` 가 effect 에서 `reset()` 후
  // 색상을 넣기 전까지 `undefined` 라, camo PDP 의 첫 화면에 Ash 배지가 떴다 하이드레이션
  // 후 바뀐다 — 이 블록이 없애려던 증상이 첫 페인트로 좁혀진 것뿐이다.
  // ash↔onyx 는 별도 제품이 아니라 SKU 옵션이라 store 가 유일한 출처다.
  const isCamo =
    product.slug === ProductSlug.TACTSUIT_AIR_CAMO || selectedColor === "camo";
  const badgeProduct =
    originalBadges.find((p) => p.slug.includes("camo") === isCamo) ??
    (originalBadges.length === 0 ? badgeAccessories[0] : undefined);
  // ⚠️ **sku 문자열이 아니라 `options` 로 고른다.** `original-badge-for-tactsuit-air` 는
  // sku 이름과 색상이 어긋나 있다(`…-onyx-og` 의 options 가 `["Ash"]`). 다만
  // `options` 와 `thumbnail` 은 서로 맞으므로(Ash → Ash-OG) 옵션 기준이면 정확하다.
  // sku 이름으로 매칭하면 정반대 이미지가 나온다.
  // 옵션 값 대소문자도 제품마다 다르다(`Onyx` vs `onyx`) — `skuOptionHasColor` 가 흡수.
  // camo 배지는 옵션이 비어 있어 `skuOptions[0]` 폴백.
  const badgeSku =
    (selectedColor &&
      badgeProduct?.skuOptions.find((o) =>
        skuOptionHasColor(o, selectedColor),
      )) ||
    badgeProduct?.skuOptions[0];
  // `??` 가 아니라 `||` — mgmt 에서 썸네일이 비면 `undefined` 가 아니라 **빈 문자열**로
  // 온다(`thumbnail: ""`). `??` 는 빈 문자열을 통과시켜 `<Image src="">` 가 된다.
  const badgeImage =
    badgeSku?.thumbnail || "/images/tactsuit/benefit/badge_00@1x.png";

  // 카드 폭이 **카드 수에 따라** 달라져서(1440 기준 4장 332 / 3장 451 / 2장 464)
  // 고정 `sizes` 로는 어느 한쪽이 반드시 틀린다.
  // ⚠️ **폭 규칙의 단일 출처는 `BENEFIT_LAYOUT` 이다.** 여기서 `benefitCardCount` 로
  // 폭을 다시 유도하면 `layout.item` 과 갈린다 — 실제로 2장(`w-[464px]` 고정)일 때
  // 공식은 708 을 내놔 **50% 과대신고**했다. `deskPx` 가 있으면 그 값을 그대로 쓴다.
  // 마지막 칸 — 번들 전용 카드가 있으면 그것, 없으면 배지 카드(배지를 붙일 수 있는 제품만)
  const showBadgeCard = !featuredCard && badgeAccessories.length > 0;
  const benefitCardCount =
    2 + (isKorea ? 1 : 0) + (showBadgeCard || featuredCard ? 1 : 0);
  const benefitGutters = 24 * (benefitCardCount - 1);
  const layout = BENEFIT_LAYOUT[benefitCardCount] ?? BENEFIT_LAYOUT_FALLBACK;
  const benefitCardSizes = [
    // 고정 폭이면 뷰포트와 무관하게 그 값. 아니면 (컨테이너 − gap 합) / 카드 수
    // (컨테이너 = min(1440, 100vw−40) 이라 1480 에서 상한이 걸리기 시작한다).
    ...(layout.deskPx
      ? [`(min-width: 1024px) ${layout.deskPx}px`]
      : [
          `(min-width: 1480px) ${Math.floor((1440 - benefitGutters) / benefitCardCount)}px`,
          `(min-width: 1024px) calc((100vw - ${40 + benefitGutters}px) / ${benefitCardCount})`,
        ]),
    // 태블릿은 `mx-4`(32) + gap 24×(열−1)
    `(min-width: 768px) calc((100vw - ${32 + 24 * (layout.cols - 1)}px) / ${layout.cols})`,
    // 모바일은 캐러셀이라 카드 폭이 **280 고정**이다(피그마 28753:31552).
    "280px",
  ].join(", ");

  // 두 트리(태블릿 이상 · 모바일 캐러셀)가 공유하는 카드 목록.
  const cardProps = { sizes: benefitCardSizes, itemClassName: layout.item };
  const cards = (
    <>
      {isKorea && (
        <BenefitCard
          {...cardProps}
          /* 시안(28718:163990 `Cover`)의 일러스트로 교체(#5287). 종전 `benefit_shipping_kr.png`
             은 **다른 그림**이었다 — 달리는 트럭 + "무료 배송"(공백 있음), 배경 흰색.
             시안은 원형 아이콘 + "무료배송"(공백 없음), 배경 Blue/10(#EDF5FF).

             ⚠️ **홈 카드와 공유하는 자산**이다. 같은 일러스트를 #5273 이 홈용으로 먼저 커밋했고
             (464×342, 국문 보드 29007:93277), PDP 노드 판(342×264)과 path 23개가 같고 좌표만
             다르다 = 프레임만 다른 동일 아트라 새로 뽑으면 두 벌이 된다.
             종전엔 `home/main-renewal` 을 그대로 가리켰는데, 그 이름이 소비자를 홈 하나로
             지목해서 리뉴얼 자산 정리 대상으로 읽혔다 → `images/common` 으로 옮겼다.
             경로·프레임(464×342)·배경은
             `__tests__/components/benefit-shipping-image.test.ts` 가 잠근다. */
          image="/images/common/benefit-shipping.svg"
          badge={t("Korea only", { defaultValue: "Korea only" })}
          title={t("Free Shipping in Korea", {
            defaultValue: "Free Shipping in Korea",
          })}
          description={t(
            "Free shipping across South Korea — no extra delivery costs.",
            {
              defaultValue:
                "Free shipping across South Korea — no extra delivery costs.",
            },
          )}
        />
      )}
      <BenefitCard
        {...cardProps}
        image="/images/tactsuit/common/benefit_01_desktop@2x.jpg"
        /* ⚠️ 키는 영문 "45 Day" 로 고정이고 locale 별 값이 다르다 —
             KR 은 "14일 환불 보장"(환불 기간이 14일). docs/refund-policy.md */
        title={t("45 Day Money Back Guarantee")}
        description={t(
          "Try your bHaptics Haptic Suit for 45 days with our easy money-back guarantee.",
        )}
        warning={t("Return shipping costs are the customer’s responsibility.")}
        link={{
          label: t("Return and Refund"),
          href: "/legals/cancellation-return-refund",
        }}
      />
      <BenefitCard
        {...cardProps}
        image="/images/tactsuit/common/benefit_02_desktop@2x.jpg"
        /* ⚠️ 피그마 표기는 "1Year"(공백 없음)지만 **기존 키를 쓴다** —
             `PurchaseBenefits.tsx` 의 /tactsuit·/tactsuit/bundles 가
             "1 Year bHaptics Warranty" 를 쓰고 두 키의 번역값이 다르다
             (kr: "보증 기간 1년" vs "1년 보증 제공"). 피그마를 따르면 같은 혜택이
             페이지마다 다른 문구로 나온다. */
        title={t("1 Year bHaptics Warranty")}
        description={t(
          "Covers manufacturing defects and hardware issues under normal use for one year from the purchase date.",
        )}
        link={{
          label: t("Learn More"),
          href: "/legals/?id=warranty",
        }}
      />
      {/* 배지를 추가할 수 있는 제품(TactSuit 계열)에서만 노출. 링크를 누르면
            배지 추가 아코디언이 열리고 그 위치로 스크롤한다(피그마 28683:7792 주석). */}
      {featuredCard && <BenefitCard {...cardProps} {...featuredCard} />}
      {showBadgeCard && (
        <BenefitCard
          {...cardProps}
          image={badgeImage}
          // ⚠️ 패딩이 **비율**이다. 고정 `p-10`(40px)은 카드가 좁아질수록 비중이 커져
          // 배지가 급격히 작아진다 — 실측 배지 영역이 커버 대비 1920 에서 64% 인데
          // 768 에선 36%(149×98)까지 떨어졌다. 퍼센트 패딩은 사방 모두 컨테이너
          // **폭** 기준이라 어느 폭에서든 같은 비율을 유지한다(8.6% × 464 = 40).
          // 피그마 배지 카드는 디자인된 이미지를 `object-cover` 로 꽉 채우는데,
          // 우리는 mgmt 제품 썸네일(투명 PNG)을 쓰므로 `object-contain` + 여백이다.
          imageClassName="object-contain p-[8.6%]"
          title={t("Free Original Badge Inside", {
            defaultValue: "Free Original Badge Inside",
          })}
          description={t(
            "Style your TactSuit from day one with a free original magnet badge.",
            {
              defaultValue:
                "Style your TactSuit from day one with a free original magnet badge.",
            },
          )}
          link={
            onAddBadge
              ? {
                  label: t("Add More Badges", {
                    defaultValue: "Add More Badges",
                  }),
                  onClick: onAddBadge,
                }
              : undefined
          }
        />
      )}
    </>
  );

  return (
    // ⚠️ **하단 패딩을 두지 않는다.** 다음 섹션(`ImmersionBeyondComparison`)이 이미
    // `Gap`(= `mt-16 laptop:mt-24` = 64/96)으로 간격을 만드는데, 여기 `pb-28`(112)이
    // 남아 있어 데스크톱에서 **208px** 가 됐다(피그마 96). 이 블록이 페이지 맨 아래였던
    // 시절의 잔재다. 섹션 간 간격은 `Gap` 하나가 소유한다.
    <div className="mt-16 laptop:mt-24">
      {/* 착용샷 히어로 — Precise Directional Feedback 카피를 타이틀 형식으로 (기존 섹션 이미지 활용) */}
      {/* 히어로 — **데스크톱은 1440 카드형**(피그마 28713:134646: w1440 h400,
          radius 16, 좌패딩 96, 카피 좌측 정렬·세로 중앙), 모바일은 풀블리드 유지
          (모바일 프레임 28718:163982 도 360 풀블리드). 그래서 컨테이너·정렬·타이포가
          전부 `laptop:` 로 갈린다. */}
      {heroOverride}
      {!heroOverride && bannerDesktop && (
        <div className="flex w-full justify-center">
          {/* ⚠️ **모바일과 데스크톱이 구조가 다르다.**
              - 모바일(28718:166005): 커버 360×240 **위에 아무것도 얹지 않고**, 바로 아래
                다크 텍스트 블록(`bg-coolgray-90`, px16 py24, gap8, 좌측 정렬).
              - 데스크톱(28713:134646): 1440 카드형 배너에 카피를 **좌측 오버레이**.
              트리를 두 벌 만들지 않고, 텍스트 컨테이너를 모바일 `static`(이미지 아래 흐름)
              → `tablet:absolute inset-0`(오버레이)로 전환해 한 벌로 처리한다.
              ⚠️ 전환점은 **`tablet`(768) 하나**다. `laptop`(1024)에 두면 768–1023 이
              "커버는 데스크톱처럼 커지는데 텍스트는 모바일 다크 블록" 인 어중간한 상태가
              된다(실측 768 에서 커버 768×512). 데스크톱 좌패딩만 96 이 과해서 태블릿은 32. */}
          <section className="relative w-full overflow-hidden tablet:h-[400px] laptop:mx-5 laptop:max-w-screen-max laptop:rounded-2xl">
            {/* 커버 — 모바일 360×240(3:2), 데스크톱은 섹션(400) 을 채운다. */}
            <div className="relative aspect-[3/2] w-full tablet:absolute tablet:inset-0 tablet:aspect-auto">
              <Image
                src={bannerDesktop}
                alt={product.name}
                fill
                // ⚠️ 데스크톱은 `laptop:max-w-screen-max`(1440) + `laptop:mx-5`(40) 라
                // 실제 폭이 `min(1440, vw−40)` 다. `100vw` 로 두면 2560 DPR1 모니터가
                // 1920w 면 될 걸 3840w 후보로 받는다(실측 88KB vs 27KB avif).
                // 1480 이 상한이 걸리기 시작하는 지점. #5089 와 같은 과대신고 클래스.
                sizes="(min-width: 1480px) 1440px, 100vw"
                className="hidden object-cover tablet:block"
              />
              <Image
                src={bannerMobile}
                alt={product.name}
                fill
                sizes="100vw"
                className="object-cover tablet:hidden"
              />
              {/* ⚠️ 스크림은 **데스크톱 전용**이다. 카피가 이미지 위에 얹히는 건 데스크톱뿐이고,
                모바일은 카피가 이미지 밖(다크 블록)이라 필요 없다.
                ⚠️ 피그마 노드엔 스크림이 없다(배경 이미지만). 그래도 남기는 이유: 이 히어로는
                제품 7종이 각자 다른 배너를 쓰는데, 밝은 이미지에서 흰 카피가 읽히려면 보정이
                필요하다. TactSuit Air 배너는 `ProductBanner` 가 아예 글자색을 어둡게 쓸 정도로
                밝다(`isAir && "text-coolgray-90"`). 지우려면 7종 배너를 전부 확인한 뒤에.
                방향은 카피 위치를 따라 **좌→우**(데스크톱 카피가 좌측 세로중앙). */}
              <div className="absolute inset-0 hidden tablet:block tablet:bg-gradient-to-r tablet:from-black/70 tablet:via-black/30 tablet:to-transparent" />
            </div>
            {/* 텍스트 — 모바일은 이미지 아래 다크 블록, 데스크톱은 좌측 오버레이.
                줄 간격 8(모바일) / 16(데스크톱). 정렬은 양쪽 모두 **좌측**이다
                (모바일 `items-start` 28718:166007 · 데스크톱 좌측 정렬 28713:134647).
                ⚠️ 피그마 모바일 블록엔 `backdrop-blur-[30px]` 가 있지만 배경이 불투명
                `#21272A` 라 시각적으로 no-op 이다 — 넣지 않는다. */}
            <div className="flex flex-col gap-2 bg-coolgray-90 px-4 py-6 tablet:absolute tablet:inset-0 tablet:justify-center tablet:gap-4 tablet:bg-transparent tablet:px-8 tablet:py-0 laptop:px-24">
              {/* eyebrow — 양쪽 모두 14/18 w400 ls.25 흰색
                (모바일 28718:166008 · 데스크톱 28713:134652). */}
              <p className="text-base tracking-[0.25px] text-white">
                {hero?.eyebrow ?? product.name}
              </p>
              <HeroCopy slug={product.slug} />
            </div>
          </section>
        </div>
      )}

      {/* 구매 혜택 — 부담 없이 체험 (홈 프로토타입 bf-card 스타일) */}
      {/* ⚠️ 컨테이너가 **1440** 이다(피그마 28707:130664 x=240 w=1440) — 하단
          마케팅 섹션 대부분은 1196(`screen-mdl`)인데 앞쪽 3개 섹션만 1440. */}
      {/* ⚠️ **`px-4` 를 붙이지 않는다.** `Container` 가 이미 `mx-4 laptop:mx-5` 를
          갖고 있어 좌우 여백이 겹쳐 쌓인다(모바일 32, 데스크톱 36). 그러면 폭이
          `min(1440, 100vw−72)` 가 돼, 바로 위 히어로(`mx-5` → `min(1440, 100vw−40)`)와
          **1512px 미만에서 좌측 엣지가 16px 어긋난다**(1440 뷰포트 실측 히어로 1400 /
          혜택 행 1368). 모바일 거터도 32 가 돼 레일 스펙(360 화면 콘텐츠 328)과 갈렸다.
          간격은 `Container` 하나가 소유한다. */}
      <Gap>
        {/* ⚠️ `min-w-0` — 모바일 캐러셀 트랙(카드 280×N)의 min-content 가
            `Container` 의 `w-full + mx-4` 를 밀어내 **좌우 16px 패딩이 통째로
            사라졌다**(실측: 섹션 박스 0..390, 카드가 0 에서 시작). flex 아이템의
            `min-width:auto` 를 0 으로 눌러야 축소가 걸린다.
            피그마 모바일 섹션은 x=16 w=328 로 좌우 16 패딩 안에 있고,
            다음 카드는 화면 끝이 아니라 그 328 경계에서 잘린다(28753:30721). */}
        <Container className="min-w-0">
          {/* 모바일은 **좌측 정렬**이다(28753:30721 루트 `items-start`).
              레이아웃이 캐러셀로 갈리는 지점과 같은 `tablet` 에서 가운데로 바뀐다.
              ⚠️ 같은 화면의 `My TactSuit Pro` 는 모바일에서도 가운데라(28718:164103)
              공용 `SectionHeading` 기본값을 바꾸면 안 된다. */}
          <SectionHeading className="text-left tablet:text-center">
            {t("bHaptics Purchase Benefits")}
          </SectionHeading>
          {/* 카드 수는 **2~4** 로 가변이다(KR 무료배송 · 배지 노출 여부). 배지가 없고
              비한국이면 2장까지 내려간다. 폭 규칙은 `BENEFIT_LAYOUT` 참고.
              ⚠️ 카드 JSX 는 **소스에서만** 한 벌이다 — 아래 두 트리(태블릿 이상
              grid/flex · 모바일 캐러셀)가 공유해 카드가 늘 때 한쪽만 고치는 사고를 막는다.
              **DOM 에는 두 벌 다 마운트된다**(`hidden tablet:grid` / `tablet:hidden` 은
              CSS 토글일 뿐). 이미지 이중 요청은 없다 — `src`·`sizes` 가 같아 같은 srcset
              후보로 풀리고, `display:none` 인 lazy 이미지는 애초에 로드되지 않는다. */}
          {/* ── 태블릿 이상 ──
              ⚠️ `items-start` 다. 피그마 3개 프레임(2·3·4장) 모두 `Lists` 가
              `items-start` 이고, 카드 높이가 제각각이다(4장 기준 392/452/426/408).
              본문 줄 수가 달라 CTA 가 계단식으로 어긋나는 게 **의도**다.
              한때 `items-stretch` 로 맞춰뒀는데 그건 피그마와 다르다.
              ⚠️ `flex-1` 은 `flex-basis:0` 이라 `flex-wrap` 으로는 줄바꿈되지 않는다.
              태블릿 다단은 래핑이 아니라 **display 전환**(`grid` → `laptop:flex`)이다.
              ⚠️ `items-start` 를 **양쪽 breakpoint 에** 건다. grid 는 `align-items`
              기본이 `normal`(= stretch) 이라 `laptop:` 에만 걸면 768–1023 에서만
              카드가 등높이가 되고 CTA 가 정렬돼, "계단식이 의도"가 그 구간만 반대가 된다
              (실측 900 편차 0 / 1100 편차 36). */}
          <div
            className={cn(
              "mt-8 hidden gap-6 tablet:grid tablet:items-start laptop:flex laptop:items-start",
              layout.cls,
            )}
          >
            {cards}
          </div>

          {/* ── 모바일: 가로 캐러셀 (피그마 28753:30721) ──
              카드 280 고정 · 간격 16 · 커버 280×220. `enableNavigations={false}` 로
              화살표를 끈다(모바일 전용이라 필요 없다).

              ⚠️ **우측은 화면 끝까지 흘려보낸다**(`-mx-4` 로 `Container` 여백 상쇄).
              `/games` 의 `DisplayNewGames` 와 같은 방식이다 — 다음 카드가 화면
              가장자리에서 잘려야 "더 있다" 가 읽힌다.
              ⚠️ 좌측 16 은 **슬라이드마다** 갖는다(`pl-4` 가 카드에). 트랙에 주면
              스크롤하는 순간 그 여백이 왼쪽으로 밀려나가 카드가 화면 끝에 붙는다
              (실측: 한 칸 스크롤 시 좌측 16 → 0). `DisplayNewGames` 가 슬라이드별
              `pl-4` 를 쓰는 이유다. 트랙의 `mr-4` 는 스크롤 끝에서 마지막 카드 우측 16.
              피그마 프레임(328)은 섹션 폭에서 잘려 그려져 있을 뿐, 우측을 328 에
              가두라는 뜻이 아니다 — 가두면 마지막 카드에만 있어야 할 여백이
              스크롤 내내 붙어 있게 된다.
              ⚠️ 트랙도 `items-start` 다(피그마 `Lists` 와 동일). 빼면 flex 기본
              stretch 로 카드가 등높이가 돼, 위 두 트리와 정렬 규칙이 갈린다. */}
          <DisplayGameCardsCarousel
            className="-mx-4 mt-6 tablet:hidden"
            enableNavigations={false}
          >
            <div className="mr-4 flex items-start">{cards}</div>
          </DisplayGameCardsCarousel>
        </Container>
      </Gap>
    </div>
  );
};

/**
 * 화이트리스트에 없는 제품 — mgmt 에 새로 등록된 장비가 여기로 온다.
 *
 * 공용 블록(45일 보장·워런티·KR 무료배송·무료 배지)에 **`In the Box` 를 더한다.**
 * 종전에는 히어로만 그려서, 새 제품에 `ProductInTheBoxData` 키를 넣어도 그 PDP 에는
 * 섹션이 안 나왔다 — DK3 와 같은 실패 모드가 한 단계 위에 있었다(#5340 리뷰).
 *
 * ⚠️ 데이터가 없는 제품에는 아무것도 안 그린다 — `ProductsInTheBox` 가 항목을 전부
 * 걸러내면 `null` 을 돌려준다(`.filter((p) => !!p.productData)`). 즉 미등록 제품에
 * 무해하고, 데이터가 생기는 순간 자동으로 나온다.
 *
 * 간격은 이 파일의 관례대로 `Gap` 을 쓴다 — DK3 분기만 예외인데, 그쪽 컨테이너가 이미
 * `gap-y-16 laptop:gap-y-24` 를 줘서 `Gap` 을 얹으면 두 배가 된다.
 */
const DefaultFeatureSections = ({
  product,
  products,
  onAddBadge,
}: {
  product: Product;
  products: Product[];
  onAddBadge?: () => void;
}) => {
  const boxProducts = useInTheBoxProducts(product);

  return (
    <div>
      <ProductHeroAndBenefits
        product={product}
        products={products}
        onAddBadge={onAddBadge}
      />
      <Gap className="px-4">
        <ProductsInTheBox products={boxProducts} />
      </Gap>
    </div>
  );
};

/**
 * 쿨링 팬 for TactSuit Pro (BHTP01A1D).
 *
 * ⚠️ 발행일은 여기 적지 않는다 — 2026-09-09 시점 요청서에는 09-18 이었는데 그런 날짜는
 * 주석에 박아 두면 조용히 낡는다. 일정은 PR·이슈에서 본다.
 *
 * ## 왜 `default` 로 두면 안 되나 — 소프트웨어가 빠진다
 *
 * `default`(`DefaultFeatureSections`)는 **히어로·혜택 + 구성품**뿐이다. 시안에 있는
 * 「비햅틱스 소프트웨어」 3카드는 **제품별 분기가 각자 넣는다** — 이 파일의 컴포넌트
 * 본문을 잘라 재보면 그렇다(2026-09-09 실측):
 *
 * ```
 *                                 소프트웨어  구성품  스펙
 * DefaultFeatureSections              ·        ✓      ·
 * TactsuitProFeatureSections          ✓        ·      ✓
 * AccessoryDeviceFeatureSections      ✓        ·      ✓
 * TactGloveDk3FeatureSections         ✓        ✓      ·
 * ```
 *
 * 등록 직후 실측한 PDP 섹션 목록에도 「비햅틱스 소프트웨어」가 **없었다**(`Product Info` ·
 * `bHaptics 구매 혜택` · `FAQ` 만). 그래서 이 `case` 가 필요하다.
 *
 * ⚠️ **`AccessoryDeviceFeatureSections`(Visor·Feet)를 재사용하지 않는다.** 그쪽은 피처
 * 카드·설치 가이드·사이즈 가이드를 전제하는데 쿨링 팬 시안에는 그 섹션이 없고, 대신
 * 제품 소개 2 · 사용 방법 3 · 충전 상태 2 · 치수도가 있다. 시안이 다른데 분기를 공유하면
 * 한쪽에 없는 섹션이 딸려 나온다 — 그 파일 주석이 Visor·Feet 사이에서 겪은 사고다.
 *
 * ## 섹션 순서 — 소프트웨어가 사양 **앞**이다 (두 보드 공통)
 *
 * 2026-09-29 「완성본」 전수 재조회 · `8X5fPtMR3n9lVij5MJKaUh`:
 *
 * ```
 * Desktop `30491:52700`   소프트웨어 y=3626     <  제품 사양 y=4395
 * Mobile  `30810:38089`   소프트웨어 y=3583.5   <  제품 사양 y=4235.8
 * ```
 *
 * ⚠️ 2026-09-16~23 에는 **모바일만** `사양 → 소프트웨어` 여서 `flex` + `order` 로 폭별로
 * 뒤집었다. 완성본에서 모바일이 데스크탑 순서로 맞춰져 그 장치를 걷었다 — DOM 순서 =
 * 시각 순서 = 탭 순서가 다시 하나다.
 *
 * 나머지는 두 보드가 같다 — `히어로·혜택 → 제품 소개 → 사용 방법 → …`.
 * (FAQ·뉴스레터·푸터는 페이지 템플릿 소관.)
 *
 * ⚠️ **`ProductsInTheBox` 를 남긴다.** 시안에 구성품 섹션이 따로 없어서 지우고 싶어지는데,
 * ① `default` 가 그리던 것을 이 `case` 가 조용히 없애는 것이 되고 ② `in-the-box-section`
 * 가드가 **모든 `*FeatureSections` 가 구성품 또는 스펙을 그린다**를 요구한다. 데이터가
 * 없으면 그 컴포넌트가 `null` 을 돌려주므로(등록 직후 상태) 화면에는 안 나온다.
 */
const CoolingFanFeatureSections = ({
  product,
  products,
  onAddBadge,
}: {
  product: Product;
  products: Product[];
  onAddBadge?: () => void;
}) => {
  const boxProducts = useInTheBoxProducts(product);

  return (
    <div>
      <ProductHeroAndBenefits
        product={product}
        products={products}
        onAddBadge={onAddBadge}
      />
      <Gap>
        <CoolingFanIntroSection />
      </Gap>
      <Gap>
        <CoolingFanHowToSection />
      </Gap>
      <Gap className="px-4">
        <BhapticsCompanionSoftware />
      </Gap>
      <Gap>
        <CoolingFanSpecsSection />
      </Gap>
      <Gap className="px-4">
        <ProductsInTheBox products={boxProducts} />
      </Gap>
    </div>
  );
};

const TactsuitProFeatureSections = ({
  product,
  products,
  packageDeals,
  onAddBadge,
}: {
  product: Product;
  products: Product[];
  packageDeals: Product[];
  onAddBadge?: () => void;
}) => {
  const { t, tp } = useTrans(["tactsuit", "shop"]);

  // 아래 -mt-[100vh] 겹침은 ProInteractionA 가 캔버스를 실제로 그릴 때만 유효하다.
  // 조건은 pro-interaction-viewport 가 단일 출처로 갖는다(상수 복사 금지).
  const isInteraction = useProInteractionViewport();

  // 배지 카드 이미지는 ProductHeroAndBenefits 가 제품별로 계산한다 (여기선 불필요).

  const translatedProduct = tp(product);
  // 비교 대상 1개만 필요하다 — 카탈로그 전체를 tp() 로 복제하면 제품 수만큼
  // 객체 클론 + i18n 조회가 매 렌더 발생한다. 찾고 나서 번역한다.
  const compareBase = products.find((p) => p.slug === ProductSlug.TACTSUIT_AIR);
  const compareProducts = [
    translatedProduct,
    compareBase && tp(compareBase),
  ].filter((p): p is Product => !!p);

  return (
    <div>
      <ProductHeroAndBenefits
        product={product}
        products={products}
        onAddBadge={onAddBadge}
      />

      {/* 여기서부터 기존 제품 소개 페이지(/tactsuit/tactsuit-pro) 상세 설명과 동일 */}
      <Gap>
        <ImmersionBeyondComparison />
      </Gap>
      <ProInteractionA className="mt-16 laptop:mt-24" pageLength={5} />
      <div
        className={cn(
          "relative z-10 h-full w-full bg-white",
          isInteraction && "-mt-[100vh]",
        )}
      >
        <Container className="mt-16 flex flex-col-reverse items-center justify-end gap-[50px] laptop:mt-0 laptop:h-[800px] laptop:flex-row laptop:gap-6">
          <div className="flex w-full justify-end laptop:max-w-[708px]">
            <div className="flex w-full flex-col gap-3 laptop:mr-[135px] laptop:max-w-[451px] laptop:gap-5">
              <h2 className="text-3xl laptop:text-5xl">
                {t("Status Check At-a-Glance")}
              </h2>
              <p className="text-base laptop:whitespace-pre-line laptop:text-lg">
                {t(
                  `Easily monitor your TactSuit's status with the front LED ring. With its built-in visualizer function, you’ll enjoy real-time feedback, ensuring you're always in control and ready for action!`,
                )}
              </p>
            </div>
          </div>

          <div className="mt relative flex w-full max-w-[708px] items-center justify-center laptop:h-full">
            <Video
              src="/media/tactsuit/tactsuit-pro/tactsuit-pro-led.mp4"
              className="h-full max-h-[522px] w-full max-w-[586px]"
            />
          </div>
        </Container>

        <Container className="mt-16 flex max-w-screen-mdl flex-col items-center justify-center laptop:mt-24">
          {/* 제목→본문 20 (피그마 28718:164103 `gap-[20px]`). 데스크톱도 20. */}
          <div className="flex flex-col items-center justify-center gap-5">
            {/* 손으로 쓴 `<h2 className="text-3xl laptop:text-5xl">` 이었다. 부모가
                `items-center` 라 **한 줄일 땐** 가운데로 보이지만 `text-align` 은
                `start` 라, 제목이 감기는 순간 줄들이 좌측 정렬로 어긋난다.
                현재 7개 locale 전부 한 줄이라 드러나지 않는 잠재 결함이었다.
                공용 `SectionHeading` 이 같은 타이포(32/40 · 24/32)에 `text-center`
                까지 갖고 있으므로 그걸 쓴다. */}
            <SectionHeading>{t("My TactSuit Pro")}</SectionHeading>
            <div className="text-center">
              <p className="text-base laptop:whitespace-pre-line laptop:text-lg">
                {t(
                  `The TactSuit Pro is customizable to fit your unique style. Adjust the size and personalize it with various options, including magnet badges and shoulder decorations.`,
                )}
              </p>
              <p className="mt-3 text-base text-coolgray-50 laptop:text-lg">
                {`* ${t("The bHaptics exclusive magnet badge is available only during special events.")}`}
              </p>
            </div>
          </div>

          <div className="mt-6 grid w-full gap-4 laptop:mt-10 laptop:grid-cols-3 laptop:gap-[25px]">
            {[
              "/media/tactsuit/tactsuit-pro/pro-custom-1.mp4",
              "/media/tactsuit/tactsuit-pro/pro-custom-2.mp4",
              "/media/tactsuit/tactsuit-pro/pro-custom-3.mp4",
            ].map((src, index) => (
              <VideoCard key={index} src={src} />
            ))}
          </div>
        </Container>
        <AudioToHapticSection slug={translatedProduct.slug} />
        <Gap className="px-4">
          <WirelessFeature imageSrc={FEATURE_02_URL} />
        </Gap>

        {/* Made for Meta — Latency-Free Wireless 다음 배치 (기획 변경) */}
        <Gap className="px-4">
          <OfficiallyMetaCertified />
        </Gap>

        <Gap className="px-4">
          <BhapticsCompanionSoftware />
        </Gap>

        {/* 비교 겸 스펙 (Air vs Pro) */}
        <Gap>
          <TactsuitTechSpecs
            useComparator={true}
            product={translatedProduct}
            deals={packageDeals.map(tp)}
            compareProducts={compareProducts}
          />
        </Gap>
      </div>
    </div>
  );
};

const VideoCard = ({ src }: { src: string }) => (
  <div className="relative aspect-square overflow-hidden rounded-2xl">
    <Video
      src={src}
      className="absolute inset-0 h-full w-full object-contain"
    />
  </div>
);

const Video = ({
  className,
  src,
  ...rest
}: { className?: string } & React.ComponentPropsWithoutRef<"video">) => (
  <video
    src={src}
    className={className}
    controls={false}
    autoPlay
    loop
    playsInline
    muted
    {...rest}
  />
);

/* ---------------------------- 구매 혜택 카드 ---------------------------- */

/**
 * 한국어 로케일 전용 혜택 이미지 스왑.
 *
 * `benefit_01` 은 이미지 안에 문구가 박혀 있어 언어별 에셋이 따로 있다
 * (`benefit_01_desktop_ko@2x.jpg`). 기존 홈 구매혜택(PurchaseBenefits:138)이 하던 스왑인데
 * PDP 로 카드를 옮길 때 따라오지 않아 `/ko` 에서 영문 이미지가 노출됐다.
 *
 * ⚠️ 판정 축은 **언어**(`i18n.language`)다 — `countryCode === "KR"`(커머스 국가)가 아니다.
 * 이미지에 박힌 건 한국어 문구이므로, 미국에서 한국어로 보는 사용자도 ko 에셋이 맞다.
 */
const koBenefitImage = (image: string, isKo: boolean) =>
  isKo && image.includes("benefit_01")
    ? image
        .replace("benefit_01_desktop", "benefit_01_desktop_ko")
        .replace("benefit_01_mobile", "benefit_01_mobile_ko")
    : image;

/**
 * 카드 수별 혜택 카드 레이아웃 — 태블릿 열 수 + 데스크톱 폭 규칙.
 *
 * **데스크톱(피그마 3종 대조)**
 * - 3장(`28716:148087`) · 4장(`28707:130664`) = `Lists` 가 `flex gap-24 items-start`,
 *   Item 이 `flex-[1_0_0]` → 균등분할.
 * - **2장(`28716:155796`) 만 다르다** = `justify-center` + Item `shrink-0 w-[464px]`.
 *   `flex-1` 로 두면 1440 에서 카드가 688 이 되고 커버가 549 까지 커진다(실측).
 *
 * **태블릿(768~1023)** — 피그마에 스펙이 없다. 3장은 3열로 둬야 laptop 진입점
 * (1024, flex 3열 312)과 폭·열 수가 그대로 이어진다. 2열이면 3번째 카드가 혼자 남고
 * 1023(483) → 1024(312) 에서 폭이 급변한다. 4장은 768 에서 4열이면 168px 로 뭉개져 2열.
 *
 * ⚠️ `cls`/`item` 은 **완성된 클래스 리터럴**이어야 한다 — `tablet:grid-cols-${n}` 처럼
 * 조립하면 Tailwind 스캐너가 못 찾아 CSS 가 생성되지 않는다. 그래서 열 수를 두 번 적게
 * 되는데(`sizes` 계산엔 숫자가 필요하다), 한 객체에 묶어 **갈릴 수 없게** 한다.
 */
const BENEFIT_LAYOUT: Record<
  number,
  {
    /** 태블릿 열 수 — `cls` 의 숫자와 반드시 같아야 해서 한 객체에 묶는다. */
    cols: number;
    /** 행(트랙)에 붙는 클래스 */
    cls: string;
    /** 카드에 붙는 데스크톱 폭 규칙 */
    item: string;
    /** 데스크톱 카드 폭이 **고정**일 때만. `sizes` 가 이 값을 그대로 쓴다. */
    deskPx?: number;
  }
> = {
  2: {
    cols: 2,
    cls: "tablet:grid-cols-2 laptop:justify-center",
    item: "laptop:w-[464px] laptop:flex-none",
    deskPx: 464,
  },
  3: { cols: 3, cls: "tablet:grid-cols-3", item: "laptop:flex-1" },
  4: { cols: 2, cls: "tablet:grid-cols-2", item: "laptop:flex-1" },
};
/** `benefitCardCount` 는 구조상 2~4 뿐이라 실행되지 않는 방어값. */
const BENEFIT_LAYOUT_FALLBACK = BENEFIT_LAYOUT[2];

/**
 * 혜택 카드 하단 링크. `bHaptics Companion Software` 섹션의 `Learn More` 와
 * 같은 스펙이라 공유 상수를 쓴다 — 스타일 근거는 `pdp-text-link.ts` 참고.
 */
const BENEFIT_LINK = PDP_TEXT_LINK;

const BenefitCard = ({
  image,
  imageClassName,
  itemClassName,
  sizes,
  title,
  description,
  warning,
  link,
  badge,
}: {
  image: string;
  imageClassName?: string;
  /** 데스크톱 폭 규칙(`flex-1` vs `w-464`). `BENEFIT_LAYOUT` 이 카드 수로 정한다. */
  itemClassName?: string;
  /** 카드 폭 공식은 호출부가 안다(카드 수 의존) — `ProductHeroAndBenefits` 참고. */
  sizes: string;
  title: string;
  description: string;
  warning?: string;
  link?: {
    label: string;
    href?: string;
    onClick?: () => void;
    /** 라벨 뒤 쉐브론 ↓ (페이지 안 섹션으로 내려가는 링크 — 시안 `DetailsLink`) */
    chevron?: boolean;
  };
  /** 카드 상단 강조 칩. 현재는 KR 전용 무료배송 카드에만 쓴다(피그마 `kr-only`). */
  badge?: string;
}) => {
  const { i18n } = useTrans(["tactsuit"]);
  const src = koBenefitImage(image, i18n.language === "ko");

  return (
    // 피그마 28718:163990 — **테두리 카드가 아니다.** 커버(rounded-16)와 텍스트가
    // 분리돼 있고 텍스트는 좌측 정렬이다. 커버↔본문 20, 본문 내부 12.
    // 모바일은 캐러셀 슬라이드라 **280 고정 + shrink-0**(28753:31552).
    // 태블릿부터는 트랙(grid)·flex 가 폭을 정하므로 모바일 고정을 푼다.
    <div
      className={cn(
        "flex w-[296px] shrink-0 flex-col gap-5 pl-4 tablet:w-auto tablet:shrink tablet:pl-0",
        itemClassName,
      )}
    >
      {/* 커버 — 비율(464:360)을 따르되 **높이 상한 360**. 피그마는 커버를 `h-[360px]`
          로 못박아 카드 폭과 무관하게 높이를 고정한다(28716:148087·155796).
          - 상한이 없으면(`aspect` 만) 카드가 넓어질 때 같이 커져서 2장 제품
            (TactSleeve·TactVisor)이 1920 에서 **549px** 이 됐다(실측).
          - 반대로 높이만 고정하면(`h-[360px]`) 카드가 좁을 때 세로로 길쭉해진다
            (1024 에서 312×360). 그래서 `aspect` + `max-h` 조합이다.
          모바일은 캐러셀 슬라이드라 280×220 고정(28753:31553). */}
      <div className="relative h-[220px] w-full overflow-hidden rounded-2xl bg-coolgray-10 tablet:aspect-[464/360] tablet:h-auto tablet:max-h-[360px]">
        {/* ⚠️ SVG 는 next/image 옵티마이저가 처리하지 않는다(`dangerouslyAllowSVG` 미설정).
            `unoptimized` 를 빼면 그 카드만 이미지가 통째로 안 나온다 — 실측으로
            `/_next/image?url=….svg` 는 원본을 돌려주지 않고, 원본 경로만 `image/svg+xml` 로
            200 이다. 무료배송 카드(#5287)가 SVG 라 필요하다. `StartWithConfidence.tsx`
            (#5273)가 같은 이유로 같은 분기를 갖는다. */}
        <Image
          src={src}
          alt={title}
          fill
          sizes={sizes}
          unoptimized={src.endsWith(".svg")}
          className={cn("object-cover", imageClassName)}
        />
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-3">
          {!!badge && (
            // 12/16 w600 blue-hover-70 on blue-10, r4, px6 py2 (28753:30353)
            // (`blue-hover-70` = #0053FF. `ui/badge.tsx` 의 `new` 배지와 같은 토큰)
            <span className="w-fit rounded bg-blue-10 px-1.5 py-0.5 text-sm font-600 tracking-[0.1px] text-blue-hover-70">
              {badge}
            </span>
          )}
          {/* 22/28 w400 (`text-2xl`) */}
          <h3 className="text-2xl text-coolgray-90">{title}</h3>
          {/* 14/18 w400 ls.25 (`text-base`) */}
          <p className="text-base tracking-[0.25px] text-coolgray-90">
            {description}
          </p>
          {!!warning && (
            // 12/16 w400 ls.4 coolgray-50 (`text-sm`)
            <p className="text-sm tracking-[0.4px] text-coolgray-50">
              * {warning}
            </p>
          )}
        </div>
        {!!link &&
          (link.href ? (
            <Link href={link.href} className={BENEFIT_LINK}>
              {link.label}
            </Link>
          ) : (
            <button
              type="button"
              onClick={link.onClick}
              className={cn(BENEFIT_LINK, link.chevron && "gap-2 pr-4")}
            >
              {link.label}
              {link.chevron && (
                <ChevronDownIcon className="size-[18px] shrink-0" />
              )}
            </button>
          ))}
      </div>
    </div>
  );
};

/* --------------------------- 섹션 헤딩 --------------------------- */

const SectionHeading = ({
  children,
  className,
}: React.PropsWithChildren<{ className?: string }>) => (
  // ⚠️ 굵기 없음이 의도다. 피그마 신규안은 Bold 지만, 기존 섹션 제목(Immersion /
  // Effortless Control / Wireless 등 공유 컴포넌트)이 전부 regular 라 이 화면만 굵게
  // 하면 한 페이지 안에서 제목 굵기가 갈린다. 전체를 함께 바꿀 때 같이 올릴 것.
  // ⚠️ `tracking-tight` 를 붙이지 않는다. 피그마 `Headline/Large`(32/40)·
  // `Headline/Small`(24/32) 둘 다 **letterSpacing 0** 이다. `tracking-tight`(-0.025em)
  // 는 32px 에서 -0.8px / 24px 에서 -0.6px 이라, 같은 페이지의 다른 섹션 제목
  // (`My TactSuit Pro` · `bHaptics Companion Software` = 실측 `normal`)과 자간이 갈렸다.
  // ⚠️ **가운데 정렬이 기본이지만 섹션마다 다르다.** 모바일에서
  // `bHaptics Purchase Benefits` 는 좌측(28753:30721 `items-start`), 반면
  // `My TactSuit Pro` 는 가운데(28718:164103 `text-center`)다. 공용값을 바꾸지 말고
  // 다른 섹션은 `className` 으로 덮을 것.
  <h2
    className={cn(
      "text-center text-3xl text-coolgray-90 laptop:text-5xl",
      className,
    )}
  >
    {children}
  </h2>
);

/* ==================================================================
 * 제품별 상세 섹션 — 기존 소개 페이지(/tactsuit/*) 구성을 그대로 이식.
 *
 * 이식 시 제외하는 것 (구매 페이지에 이미 있거나 리뉴얼로 대체됨):
 *  - 상세 이미지 캐러셀("Details") → 구매 섹션의 ProductImageGallery 가 담당
 *  - PurchaseBenefits             → ProductHeroAndBenefits 의 혜택 4카드로 대체
 *  - ProductFaq                   → [slug].tsx 하단에서 이미 렌더
 *
 * ⚠️ 이 규칙은 처음부터 여기 적혀 있었지만 **Air·Air Camo 만 지키지 않았다** —
 * `ProductDetailCarousel` 을 그대로 들고 와서 색상 스와치까지 함께 렌더했다(#5266).
 * 상단 갤러리가 `?color=` 로 색상별 이미지를 이미 바꾸므로(실측: onyx `air-01` /
 * ash `air-02`) 순수 중복이었고, 그 모듈은 사용처가 0 이 되어 삭제했다.
 * 제품별 섹션을 새로 이식할 때 이미지 캐러셀을 다시 들고 오지 말 것.
 * ================================================================== */

const TactSleeveFeatureSections = ({
  product,
  products,
  packageDeals,
  onAddBadge,
}: {
  product: Product;
  products: Product[];
  packageDeals: Product[];
  onAddBadge?: () => void;
}) => {
  const { tp } = useTrans(["tactsuit", "shop"]);
  const translatedProduct = tp(product);

  return (
    <div>
      <ProductHeroAndBenefits
        product={product}
        products={products}
        onAddBadge={onAddBadge}
      />
      {/* ⚠️ `ImmersionBeyondComparison` 을 두지 않는다 — **Pro · Air · Air Camo 전용**이다
          (기획 확정, 2026-08-10). 피그마 TactSleeve 프레임에서도 이 섹션이
          `hidden`(28715:143126) 으로 꺼져 있다.
          다른 제품에 넣으려면 이 결정을 먼저 뒤집을 것. */}
      <SleeveProductFeatures />
      <AudioToHapticSection slug={translatedProduct.slug} />
      <Gap className="px-4">
        <WirelessFeature imageSrc={FEATURE_02_URL} />
      </Gap>
      <Gap className="px-4">
        <BhapticsCompanionSoftware />
      </Gap>
      <Gap>
        <div id={ComponentId.TACTSUIT_TECH_SPECS} />
        <TactsuitTechSpecs
          product={translatedProduct}
          deals={packageDeals.map(tp)}
        />
      </Gap>
    </div>
  );
};

/**
 * TactVisor · Tactosy for Feet — 두 소개 페이지가 동일 구성이라 한 컴포넌트로 처리.
 * (FeatureCards / Summary / InstallationGuide / TechSpecs)
 */
const AccessoryDeviceFeatureSections = ({
  product,
  products,
  packageDeals,
  onAddBadge,
}: {
  product: Product;
  products: Product[];
  packageDeals: Product[];
  onAddBadge?: () => void;
}) => {
  const { tp } = useTrans(["tactsuit", "shop"]);
  const translatedProduct = tp(product);
  const features = useProductFeatures(translatedProduct.slug);

  return (
    <div>
      <ProductHeroAndBenefits
        product={product}
        products={products}
        onAddBadge={onAddBadge}
      />
      {features && (
        <Gap>
          <FeatureCards features={features} />
        </Gap>
      )}
      {/* ⚠️ **Immersion 은 이 분기(Visor·Feet)에 넣지 않는다.** 두 시안 모두 그 섹션이
          `hidden="true"` 다 — Visor `28716:145108` · Feet `28716:147109`. 프레임에
          노드가 남아 있어 **이름만 보면 "있다" 로 오독하기 쉽다**(#5266 에서 실제로
          Visor 에 잘못 추가했다가 되돌렸다). Air·Camo 시안은 노출이라 그쪽만 렌더한다.

          시안에서 섹션 유무를 볼 때는 노드 존재가 아니라 **`hidden` 속성까지** 확인할 것. */}
      {/* ⚠️ **Visor 전용.** "Upgrade Your Content Experience"(호환 게임 카드 + 탭)는
          Visor 시안에만 있고(`28716:155167`, y=6258) **Feet 시안에는 없다**
          (`28716:146843` 통독: 피처 3개 → Companion → Tech Specs 로 바로 간다).
          두 제품이 이 분기를 공유해서 Feet 에도 딸려 나오고 있었다.

          ⚠️ 두 시안은 이 섹션 말고도 갈린다 — Immersion 은 반대로 **Visor 만 노출**이고
          Feet 은 `hidden` 이다(아래 주석 참조). "Visor=Feet" 로 가정하고 공통 섹션을
          추가하지 말 것. */}
      {translatedProduct.slug === ProductSlug.TACTVISOR && (
        <BhapticsContentCards
          deviceType={ContentsService.slugToFilters(translatedProduct.slug)}
        />
      )}
      {/* ⚠️ Companion Software 는 **설치 가이드보다 앞**이다 — 두 시안 모두 그렇다.
            Visor(`28716:144842`) 피처 → Tabs → **Companion y=7018** → Installation 7805 → SizeGuide 8327
            Feet (`28716:146843`) 피처 3개  → **Companion y=6258** → Tech Specs 7045
          예전엔 Installation·SizeGuide·Summary 뒤에 있어 세 섹션만큼 늦었다.
          Pro·Air 분기도 Companion 을 TechSpecs 직전에 두므로 제품 간 위치도 맞는다. */}
      <Gap className="px-4">
        <BhapticsCompanionSoftware />
      </Gap>
      <Gap className="mx-auto max-w-screen-mdl px-4">
        <InstallationGuide slug={translatedProduct.slug} />
      </Gap>
      {translatedProduct.slug === ProductSlug.TACTVISOR && (
        <Gap className="mx-auto max-w-screen-mdl px-4">
          <TactvisorSizeGuide />
        </Gap>
      )}
      {/* ⚠️ 이 `Summary`(아이콘 그리드)가 시안의 **"Tech Specs" 섹션**이다
          — Feet `28716:155750`(y=7045) · Visor `28716:155650`(y=8737).
          헤더("Tech Specs")는 `Summary` **안**에 있다 — 데이터 없는 제품에서 헤더만
          남지 않도록 그 컴포넌트의 null 가드에 묶었다.

          ⚠️ 헷갈리기 쉬운 이름 충돌: 바로 아래 `TactsuitTechSpecs` 안의 `TechSpecs` 는
          **다른 섹션**이다 — 그건 `{제품명} Specs`(Feet y=7769 "Tactosy for feet Specs")
          제목을 스스로 그린다. 이름만 보고 중복이라 판단해 지우지 말 것. */}
      <Gap className="mx-auto max-w-screen-mdl px-4">
        <Summary id={translatedProduct.slug} className="laptop:pt-5" />
      </Gap>
      <Gap>
        <div id={ComponentId.TACTSUIT_TECH_SPECS} />
        <TactsuitTechSpecs
          product={translatedProduct}
          deals={packageDeals.map(tp)}
        />
      </Gap>
    </div>
  );
};

const TactGloveDk3FeatureSections = ({
  product,
  products,
  onAddBadge,
}: {
  product: Product;
  products: Product[];
  onAddBadge?: () => void;
}) => {
  const boxProducts = useInTheBoxProducts(product);

  return (
    <div>
      <ProductHeroAndBenefits
        product={product}
        products={products}
        onAddBadge={onAddBadge}
      />
      {/* DK3 는 전용 섹션 컴포넌트 세트를 그대로 사용 (소개 페이지와 동일 순서).
        Dk3Faq 는 [slug].tsx 의 ProductFaq 와 중복이라 제외.

        ⚠️ `Dk3Details`("Details" 제품 이미지 캐러셀)도 제외했다 — **Glove 시안
        (`28716:145848`)에 대응 섹션이 없다.** 시안에서 "Details" 로 검색되는 노드는
        스펙 표의 `Size Details` 라벨 하나뿐이고, 본문은 Purchase Benefits(3512) 다음이
        바로 `Turn Your Palm Into a Haptic Canvas`(4212)다.
        Air 와 같은 이유다 — 상단 갤러리가 제품 이미지를 이미 담당한다(이식 제외 규칙은
        이 파일 상단 주석 참조). 사용처가 이 한 곳뿐이라 모듈도 삭제했다. */}
      <div className="mt-16 flex flex-col gap-y-16 laptop:mt-24 laptop:gap-y-24">
        {/* VRChat 영상 섹션(#6025) — 혜택 블록 바로 다음, 기술 소개 앞. 간격은 이 컨테이너의
          gap 이 준다(`<Gap>` 으로 감싸면 두 겹이 된다). OVERVIEW 앵커는 기술 소개에 그대로 둔다. */}
        <Dk3VrchatStory />
        <div id={ComponentId.OVERVIEW}>
          <Dk3Technology />
        </div>
        <Dk3Comparison />
        <Dk3Performance />
        <Dk3UseCases />
        {/* ⚠️ Companion Software 가 **DK3 분기에만 빠져 있었다.** 7개 제품 중 이 분기만
          렌더하지 않아서 글러브 PDP 에서 플레이어 안내가 통째로 없었다.
          시안(Glove `28716:145848`)에는 `bHaptics Companion Software`(y=8775)가 있다.

          ⚠️ **정확한 삽입 위치는 y 좌표로 못 정했다** — Glove 프레임은 섹션들이 서로
          겹쳐 있어(8120/8419/8775/9177/9562) 순서를 단정할 수 없다. 그래서 좌표 대신
          **다른 분기의 관례**를 따랐다: Pro·Air·Visor/Feet 모두 Companion 을 TechSpecs
          직전에 둔다. 시안에서도 Companion 이 specs 표(9177) 앞이라 어긋나지 않는다.

          ⚠️ 다른 분기처럼 `<Gap>` 으로 감싸면 **간격이 두 배가 된다** — 이 컨테이너는
          이미 `gap-y-16 laptop:gap-y-24` 로 자식 간 간격을 주고 있어서 `Gap` 의
          `mt-16 laptop:mt-24` 가 그 위에 더해진다. 좌우 패딩만 맞춘다. */}
        <div className="px-4">
          <BhapticsCompanionSoftware />
        </div>
        <div id={ComponentId.TACTSUIT_TECH_SPECS}>
          <Dk3Specs />
        </div>
        <Dk3SizeGuide />
        {/* ⚠️ **DK3 분기만 이 섹션이 없었다.** `In the Box` 는 `TactsuitTechSpecs` 안에
          있는데(#5316) 글러브는 스펙을 전용 컴포넌트(`Dk3Specs`)로 그려 그걸 렌더하지
          않는다. 그래서 데이터(`ProductInTheBoxData[TACTGLOVE_DK3]`)가 있는데도 글러브
          PDP 에서만 섹션이 통째로 없었다 — #5316 이 고치려던 제품이 유일하게 안 고쳐진
          상태였다. 다른 4개 분기(Pro·Sleeve·Visor/Feet·Air)는 그 컴포넌트를 그린다.

          위치는 시안 기준 — Glove 프레임(`28716:145848`)에서 `In the box`(`28718:171748`)는
          y=11057 로 **사이즈 가이드(10641) 다음, `You may also like`(11549) 앞**이다.
          이 구간은 상단 섹션들처럼 좌표가 겹치지 않아 순서가 분명하다.

          `products` 는 `TactsuitTechSpecs` 와 **같은 훅**을 쓴다 — 번들 선택(`?bundle=`)
          동작이 분기마다 갈리지 않게 한다. 좌우 패딩만 맞춘다(이 컨테이너가 이미
          `gap-y-16 laptop:gap-y-24` 로 간격을 준다 — `Gap` 을 쓰면 두 배가 된다). */}
        <div className="px-4">
          <ProductsInTheBox products={boxProducts} />
        </div>
      </div>
    </div>
  );
};

/**
 * TactSuit Air · Air: Camo — 소개 페이지(/tactsuit/tactsuit-air)의 색상별 콘텐츠 컴포넌트를 이식.
 * camo 여부는 상위(`[slug].tsx`)가 effectiveProduct 로 판단해 넘겨주므로 여기선 slug 로만 분기한다.
 *
 * ⚠️ **색상은 여기로 안 내려온다**(#5266). 예전엔 본문에 "Details" 블록(색상별 이미지
 * 캐러셀 + 색상 스와치)이 있어서 `selectedColor`/`onColorChange` 를 받았다. 그런데
 *   · 색상 선택 → 구매 레일의 `ProductOptionSelect`(레일 라벨 `Color: Onyx`)
 *   · 색상별 이미지 → 상단 갤러리가 `?color=` 로 이미 전환(실측: onyx `air-01` / ash `air-02`)
 * 로 둘 다 위에서 처리돼 **같은 일을 두 번 하고 있었다.** Figma(`28715:139470`)에도
 * Details 섹션이 없고, `ProductDetailCarousel` 은 Air 계열 2곳만 쓰던 예외였다.
 * 색상 상태를 다시 내려보내려면 먼저 그 중복부터 정리할 것.
 */
const TactSuitAirFeatureSections = ({
  product,
  products,
  packageDeals,
  onAddBadge,
}: {
  product: Product;
  products: Product[];
  packageDeals: Product[];
  onAddBadge?: () => void;
}) => {
  const { tp } = useTrans(["tactsuit", "shop"]);
  const translatedProduct = tp(product);
  const isCamo = product.slug === ProductSlug.TACTSUIT_AIR_CAMO;
  // 카탈로그 전체 번역 대신 대상만 찾아 번역 (위 compareBase 와 같은 이유).
  const compareProBase = products.find(
    (p) => p.slug === ProductSlug.TACTSUIT_PRO,
  );
  const comparePro = compareProBase && tp(compareProBase);

  return (
    <div>
      <ProductHeroAndBenefits
        product={product}
        products={products}
        onAddBadge={onAddBadge}
      />
      {isCamo ? (
        <TactSuitAirCamoContent />
      ) : (
        <TactSuitAirContent product={translatedProduct} />
      )}
      <Gap className="px-4">
        <BhapticsCompanionSoftware />
      </Gap>
      <Gap>
        <div id={ComponentId.TACTSUIT_TECH_SPECS} />
        <TactsuitTechSpecs
          useComparator
          product={translatedProduct}
          deals={packageDeals.map(tp)}
          compareProducts={
            comparePro ? [translatedProduct, comparePro] : [translatedProduct]
          }
        />
      </Gap>
    </div>
  );
};

/**
 * EXOSHOCK 번들 전용 PDP — 피그마 `31584:38289`(데스크톱 `31584:38290` · 모바일 `31584:38771`, 2026-10-28).
 *
 * 본문 순서(시안 y): EventBanner → Purchase Benefits → Platform → Bundle Contents → Gamekey(Variant A)
 * → Badge → Mesh Lining → Native Haptics → Companion Software → TactSuit Air Specs · In the Box.
 * FAQ 는 `[slug].tsx` 가 그린다(태그는 `bundle-detail-rail.tsx` 의 `faqTags`).
 *
 * - 히어로는 공용 1440 카드형 대신 **Full-bleed 이벤트 배너**다(시안 보드 「Hero 는 Full-bleed 로 고정」).
 * - 혜택 카드 3번째 = 배지 카드 대신 「EXOSHOCK Exclusive Badge Included」 — 링크는 본문 배지 섹션으로 내려간다.
 * - Platform 은 다른 PDP 와 공용인 `ImmersionBeyondComparison` + EXOSHOCK 안내 한 줄.
 *   ⚠️ 시안 카드는 아직 회색 자리이고, 카드를 누르면 뜨는 팝업 문구(시안 노트)도 공용 컴포넌트와 다르다 — 공용 동작을 유지한다.
 * - 스펙 표는 **TactSuit Air** 기준이다(시안 「TactSuit Air Specs」 — 번들 제품으로 그리면 스펙 데이터가 없다).
 *   미리보기(앞면 콜랩 렌더 · 캡션 · 안내)와 In the Box(EXOSHOCK 와펜 Air)는 번들 데이터다(2026-10-28 시안 재확인).
 */
const ExoshockBundleFeatureSections = ({
  product,
  products,
}: {
  product: Product;
  products: Product[];
}) => {
  const { t, tp } = useTrans(["tactsuit", "shop"]);
  // 카탈로그 전체 번역 대신 대상만 찾아 번역 (Air 분기와 같은 이유).
  const airBase = products.find((p) => p.slug === ProductSlug.TACTSUIT_AIR);
  const air = airBase && tp(airBase);
  const proBase = products.find((p) => p.slug === ProductSlug.TACTSUIT_PRO);
  const pro = proBase && tp(proBase);

  return (
    <div>
      <ProductHeroAndBenefits
        product={product}
        products={products}
        heroOverride={<ExoshockEventBanner />}
        featuredCard={{
          // 어드민 배지 구성품 SKU 썸네일 — 기본 배지 카드와 같은 출처 · 같은 폴백
          image:
            ProductUtil.getSkuThumbnail(
              products,
              EXOSHOCK_COMPONENT_SKU.badge,
            ) || "/images/tactsuit/benefit/badge_00@1x.png",
          // 시안: 커버 높이 360 안에 배지 원 ≈ 228(360 칸 inset 10% 의 79%). 어드민 이미지가 이미 1000² 에 원 63% 라
          // 여백 없이 높이에 맞추면(360) 원이 같은 크기가 된다
          imageClassName: "object-contain",
          title: t("EXOSHOCK Exclusive Badge Included"),
          description: t(
            "A collab-exclusive badge finished with a white EXS logo and a red ring. Velcro-backed — attach and detach with ease.",
          ),
          link: {
            label: t("View badge details"),
            onClick: () => scrollToDetail(BADGE_DETAIL_ANCHOR),
            chevron: true,
          },
        }}
      />
      <Gap>
        <div id={ComponentId.OVERVIEW} />
        <ImmersionBeyondComparison />
        <ExoshockPlatformNote />
      </Gap>
      <Gap className="px-4">
        <ExoshockBundleContents />
      </Gap>
      {/* 게임 키 섹션은 안쪽 상하 여백(모바일 48 · 데스크톱 96)을 스스로 갖는다(시안 Variant A) */}
      <Gap>
        <ExoshockGamekeySection />
      </Gap>
      <Gap className="px-4">
        <ExoshockBadgeSection id={BADGE_DETAIL_ANCHOR} />
      </Gap>
      <Gap className="px-4">
        <ExoshockMeshLiningSection id={MESH_LINING_DETAIL_ANCHOR} />
      </Gap>
      <Gap className="px-4">
        <ExoshockNativeHapticsSection />
      </Gap>
      <Gap className="px-4">
        <BhapticsCompanionSoftware />
      </Gap>
      {air && (
        <Gap>
          <div id={ComponentId.TACTSUIT_TECH_SPECS} />
          <TactsuitTechSpecs
            useComparator
            product={air}
            // 시안에 추가 액세서리 블록이 없다(`_archive/YouMayAlsoLike` 숨김)
            deals={[]}
            compareProducts={pro ? [air, pro] : [air]}
            // 앞면 = 콜랩 렌더 · 마커에 Badge(`previews.json` 의 exoshock-bundle) · 스펙 표는 Air
            previewSlug={product.slug}
            specsNote="Tap the markers on the product to explore each part. Specifications are based on the standard TactSuit Air; the front shows the collab product rendering."
            // In the Box 1번 = EXOSHOCK 와펜 Air(`ProductInTheBoxData` 의 exoshock-bundle)
            boxProduct={product}
          />
        </Gap>
      )}
    </div>
  );
};
